import type { AxiosInstance } from 'axios';
import type {
  Chat,
  ChatListQueryParams,
  ChatCreateRequest,
  ChatUpdateRequest,
  ChatItem,
  ChatItemCreateRequest,
  ChatItemUpdateRequest,
  CompletionRequest,
  EntityId,
} from '@/types';

export const getChats = async (
  api: AxiosInstance,
  type: string,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<Chat[]> => {
  const resp = await api.get<Chat[]>('/chat', {
    params: { type, limit, start_key, name } as ChatListQueryParams,
  });
  return resp.data;
};

export const getChat = async (
  api: AxiosInstance,
  chat_id: EntityId,
): Promise<Chat> => {
  const resp = await api.get<Chat>(`/chat/${chat_id}`);
  return resp.data;
};

export const createChat = async (
  api: AxiosInstance,
  values: ChatCreateRequest,
): Promise<Chat> => {
  const resp = await api.post<Chat>(`/chat/`, values);
  return resp.data;
};

export const updateChat = async (
  api: AxiosInstance,
  chat_id: EntityId,
  values: ChatUpdateRequest,
): Promise<Chat> => {
  const resp = await api.put<Chat>(`/chat/${chat_id}`, values);
  return resp.data;
};

export const deleteChat = async (
  api: AxiosInstance,
  chat_id: EntityId,
): Promise<void> => {
  const resp = await api.delete(`/chat/${chat_id}`);
  return resp.data;
};

export const getChatItems = async (
  api: AxiosInstance,
  chat_id: EntityId,
): Promise<ChatItem[]> => {
  const resp = await api.get<ChatItem[]>(`/chat/${chat_id}/item`);
  return resp.data;
};

export const addChatItem = async (
  api: AxiosInstance,
  chat_id: EntityId,
  values: ChatItemCreateRequest,
): Promise<ChatItem> => {
  const resp = await api.post<ChatItem>(`/chat/${chat_id}/item`, values);
  return resp.data;
};

export const updateChatItem = async (
  api: AxiosInstance,
  chat_id: EntityId,
  chat_item_id: EntityId,
  values: ChatItemUpdateRequest,
): Promise<ChatItem> => {
  const resp = await api.patch<ChatItem>(
    `/chat/${chat_id}/item/${chat_item_id}`,
    values,
  );
  return resp.data;
};

export const generateCompletion = async (
  api: AxiosInstance,
  chat_id: EntityId,
  model: string,
  messages: CompletionRequest['messages'],
  max_tokens: number,
  temperature: number,
  top_p: number,
  abortSignal: AbortSignal | null = null,
): Promise<Response> => {
  const token = localStorage.getItem('accqsure_jwt');

  const init: RequestInit = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      messages,
      model,
      max_tokens,
      temperature,
      top_p,
    }),
  };

  // Add abort signal if provided
  if (abortSignal) {
    init.signal = abortSignal;
  }

  const response = await fetch(
    `${api.defaults.baseURL}/chat/${chat_id}/completion`,
    init,
  );

  return response;
};

